<script lang="ts" setup>
const { t } = useI18n()
const api = useApi()

const { data: response, execute, status } = useAsyncData(
  'doc:utilities-fetch-openapi',
  () => api.utilities.fetchOpenApi(),
  { immediate: false },
)

const endpoints: ApiEndpoint[] = [
  {
    method: 'GET',
    path: '/openapi',
  },
]

const queryAttributes: ApiAttribute[] = []

const responseAttributes: ApiAttribute[] = [
  {
    description: t('response.components'),
    name: 'components',
    type: 'object',
  },
  {
    description: t('response.externalDocs'),
    name: 'externalDocs',
    type: 'object',
  },
  {
    description: t('response.info'),
    name: 'info',
    type: 'object',
  },
  {
    description: t('response.openapi'),
    name: 'openapi',
    type: 'string',
  },
  {
    description: t('response.paths'),
    name: 'paths',
    type: 'object',
  },
  {
    description: t('response.servers'),
    name: 'servers',
    type: 'array',
  },
  {
    description: t('response.webhooks'),
    name: 'webhooks',
    type: 'object',
  },
]

async function onVisibilityChange(visibility: boolean) {
  if (visibility && !response.value) {
    await execute()
  }
}
</script>

<template>
  <ApiDoc
    :endpoints="endpoints"
    :loading="status === 'pending'"
    :query-attributes="queryAttributes"
    :response="response?.data"
    :response-attributes="responseAttributes"
    @visibility-change="onVisibilityChange"
  />
</template>

<i18n lang="json">
{
  "en": {
    "response": {
      "components": "Reusable components used across the API specification.",
      "externalDocs": "Additional external documentation.",
      "info": "Metadata about the API.",
      "openapi": "The version of the OpenAPI specification.",
      "paths": "The available paths and operations for the API.",
      "servers": "An array of server objects, which provide connectivity information to a target server.",
      "webhooks": "Incoming webhooks that may be received as part of this API."
    }
  },
  "es": {
    "response": {
      "components": "Componentes reutilizables utilizados en toda la especificación de la API.",
      "externalDocs": "Documentación externa adicional.",
      "info": "Metadatos sobre la API.",
      "openapi": "La versión de la especificación OpenAPI.",
      "paths": "Las rutas y operaciones disponibles para la API.",
      "servers": "Una matriz de objetos de servidor, que proporcionan información de conectividad a un servidor de destino.",
      "webhooks": "Webhooks entrantes que pueden recibirse como parte de esta API."
    }
  },
  "fr": {
    "response": {
      "components": "Composants réutilisables utilisés dans toute la spécification de l'API.",
      "externalDocs": "Documentation externe supplémentaire.",
      "info": "Métadonnées sur l'API.",
      "openapi": "La version de la spécification OpenAPI.",
      "paths": "Les chemins et opérations disponibles pour l'API.",
      "servers": "Un tableau d'objets serveur, qui fournissent des informations de connectivité à un serveur cible.",
      "webhooks": "Webhooks entrants qui peuvent être reçus dans le cadre de cette API."
    }
  }
}
</i18n>
